import type { ProposalTheme } from "../types/proposalPresentation";
import type { AnnotationDoc } from "../shared/photo-annotations/annotationSchema";
import { apiClient } from "./api";
import type { Photo } from "../types";

export interface DealPhotoPage {
  results: Photo[];
  count: number;
  next: string | null;
}

export type DealType = "insurance" | "repair";
export interface RepairLibraryTask {
  id: string;
  title: string;
  location: string;
  issue: string;
  system: string;
  solution: string;
  quantity: string;
  unit: string;
  unit_price: string;
}
export interface DealSettings {
  repair_library?: RepairLibraryTask[];
  insurance_enabled: boolean;
  insurance_configured: boolean;
  insurance_terms: string;
  repair_configured: boolean;
  repair_enabled: boolean;
  contingency_template: number | null;
  repair_terms: string;
  inspection_requirements: {
    title: string;
    minimum_photos: number;
    allow_exception: boolean;
  }[];
  can_manage: boolean;
  current_user: number;
  templates: { id: number; name: string; published_version: number }[];
}
export interface DealDocument {
  id: number;
  kind:
    | "inspection"
    | "contingency"
    | "repair_authorization"
    | "manual_authorization";
  authorization?: {
    reason: string;
    recorded_by_name: string;
    recorded_at: string;
  } | null;
  version: number;
  state: string;
  error: string;
  created_at: string;
  contract_id: number | null;
  contract_status: string | null;
  contract_url: string | null;
  pdf_url: string | null;
}
export interface Finding {
  id: number;
  title: string;
  location: string;
  notes: string;
  minimum_photos: number;
  allow_exception: boolean;
  result: string;
  exception_reason: string;
  reviewed_by: number | null;
}
export interface RepairItem {
  discount_type?: "amount" | "percent";
  discount_value?: string;
  gross_amount?: string;
  discount_amount?: string;
  line_total?: string;
  id: number;
  title: string;
  location: string;
  issue: string;
  system: string;
  solution: string;
  quantity: string;
  unit: string;
  unit_price: string;
  source_item: number | null;
}
export interface DealSummary {
  revision?: number;
  id: number;
  proposal_id: number;
  proposal_number: string;
  deal_type: DealType;
  proposal_type: string;
  title: string;
  opportunity_name: string;
  contact_name: string;
  status_label: string;
  totals: {
    subtotal: string;
    tax: string;
    total: string;
    gross_subtotal?: string;
    line_discount?: string;
    proposal_discount?: string;
    discount_total?: string;
  } | null;
}
export interface DealSupportingDocument {
  id: number;
  title: string;
  category: "manufacturer" | "building_codes";
  content_type: string;
  url: string;
}
export interface Deal extends DealSummary {
  discount_type?: "amount" | "percent";
  discount_value?: string;
  brand?: ProposalTheme;
  organization_name?: string;
  property_address?: string;
  supporting_documents?: DealSupportingDocument[];
  revision: number;
  opportunity: number;
  contact_email: string;
  exclusions: string;
  terms: string;
  tax_enabled: boolean;
  tax_rate: string;
  inspection_date: string | null;
  inspection_owner: number | null;
  claim: Record<string, string | number | null> | null;
  findings: Finding[];
  repair_items: RepairItem[];
  evidence: {
    id: number;
    finding: number | null;
    repair_item: number | null;
    caption: string;
    image_url: string;
    annotations?: AnnotationDoc;
    upload_id: string;
    source_photo?: number | null;
  }[];
  readiness: { ready: boolean; issues: string[] };
  documents: DealDocument[];
  grants: {
    id: number;
    document: number;
    audience: string;
    email: string;
    expires_at: string;
    revoked_at: string | null;
    viewed_at: string | null;
    delivery_status: string;
    url: string;
  }[];
  source_id: number | null;
  job: number | null;
  construction_proposal: number | null;
  scope_locked: boolean;
  activity: {
    id: number;
    description: string;
    actor: string;
    created_at: string;
  }[];
}
export const proposalDealsApi = {
  remove: async (id: number, revision: number) =>
    apiClient.delete(`/api/proposal-deals/${id}/?revision=${revision}`),
  opportunityPhotos: async (id: number, page = 1) =>
    (
      await apiClient.get<DealPhotoPage>(
        `/api/proposal-deals/${id}/opportunity-photos/?page=${page}&page_size=24`,
      )
    ).data,
  removeItem: async (id: number, item: number, revision: number) =>
    (
      await apiClient.delete<Deal>(
        `/api/proposal-deals/${id}/repair-items/${item}/?revision=${revision}`,
      )
    ).data,
  settings: async () =>
    (await apiClient.get<DealSettings>("/api/proposal-deals/settings/")).data,
  saveSettings: async (data: Partial<DealSettings>) =>
    (await apiClient.patch<DealSettings>("/api/proposal-deals/settings/", data))
      .data,
  list: async (query = "") =>
    (
      await apiClient.get<{
        count: number;
        page: number;
        results: DealSummary[];
      }>(`/api/proposal-deals/?${query}`)
    ).data,
  get: async (id: number) =>
    (await apiClient.get<Deal>(`/api/proposal-deals/${id}/`)).data,
  create: async (data: {
    opportunity: number;
    deal_type: DealType;
    proposal_type: string;
    request_id: string;
  }) => (await apiClient.post<Deal>("/api/proposal-deals/", data)).data,
  patch: async (id: number, data: Record<string, unknown>, path = "") =>
    (await apiClient.patch<Deal>(`/api/proposal-deals/${id}/${path}`, data))
      .data,
  action: async <T = Deal>(id: number, action: string, data: unknown = {}) =>
    (await apiClient.post<T>(`/api/proposal-deals/${id}/${action}/`, data))
      .data,
};

function validationMessage(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value))
    return value.map(validationMessage).filter(Boolean).join(" ");
  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([field, detail]) => {
        const message = validationMessage(detail);
        if (!message) return "";
        return ["detail", "error", "non_field_errors"].includes(field)
          ? message
          : `${field}: ${message}`;
      })
      .filter(Boolean)
      .join(" ");
  }
  return "";
}

export function dealError(error: unknown): string {
  const failure = error as { responseData?: unknown; message?: string } | null;
  return (
    validationMessage(failure?.responseData) ||
    failure?.message ||
    "Unable to save. Your changes are still here; retry when connected."
  );
}
